// The PASSPHRASE form that protects this browser's device seed, and the
// account-passkey ceremonies (enrol / adopt) that are the other way to
// protect it. The passphrase ceremony: generate master key + seed
// envelope, wrap the master key with the passphrase, save the
// WasmDeviceRecord to IndexedDB, and unlock the Service Worker so
// subsequent device.* ops work. Caller's onDone receives the saved
// record. Appendix C (2026-09-22): the form is the FALLBACK for a browser
// that cannot do a passkey (and the Security section's legacy-import
// surface); every passkey is an account passkey, so the device-only
// passkey this file used to mint is gone.
//
// The seed itself is sourced inside setupDevice: legacy IDB device
// if one is present (so the pre-WASM seed and the WASM seed are the
// same seed), otherwise a freshly minted random 32 bytes.

import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { loadPracticemdWasm, PracticemdWASM } from './loader'
import { saveWasmDevice, loadCurrentWasmDevice, promoteWasmDevice, updateAuthenticators, WasmDeviceRecord, StoredAuthenticator } from './deviceStore'
import { deviceWorkerClient } from './deviceWorkerClient'
import {
    enrollRecoveryPasskey, wrapWithRawKey, bytesToHexString,
} from './authenticators'
import { seedToMnemonic } from '../crypto/bip39'
import { importLegacyDevice, bytesToHex} from './importFromLegacy'
import {getDeviceNickname} from "../misc/deviceNickname";

export interface DeviceUnlockSetupProps {
    onDone?: (record: WasmDeviceRecord) => void
    onError?: (err: Error) => void
    /** Optional className for the outer wrapper — lets callers style per-surface. */
    className?: string
    /** Prompt copy above the fields. */
    intro?: React.ReactNode
    /** Submit button label when idle. Defaults to "Protect this browser". */
    submitLabel?: string
}

export interface SetupDeviceOptions {
    passphrase: string
}

function hexToBytes(hex: string): Uint8Array {
    const out = new Uint8Array(hex.length / 2)
    for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    return out
}


// Silent provisioning for the anonymous /ava landing
// (practicemd-api docs/Ava-Anonymous-Landing.md): steps 1, 2 and 5 of
// setupDevice with NO authenticator. The master key is kept in the clear on
// the record so the device can be unlocked on reload and later PROMOTED
// (promoteDevice) when the visitor chooses an account. Same seed, same
// sigKid, same search — the promotion never replaces the device.
export async function provisionSilentDevice(): Promise<WasmDeviceRecord> {
    const wasm = await loadPracticemdWasm()
    let seedBytes: Uint8Array | undefined
    try {
        seedBytes = crypto.getRandomValues(new Uint8Array(32))
        const seedHex = bytesToHex(seedBytes)
        const kids = await wasm.deriveDeviceKids(seedHex)
        const gen = await wasm.generateMasterKeyAndSeedEnvelope(seedHex)
        const record: WasmDeviceRecord = {
            seedKid: kids.seedKid,
            sigKid: kids.sigKid,
            encKid: kids.encKid,
            aesKid: kids.aesKid,
            seedEnvelopeB64: gen.seedEnvelopeB64,
            authenticators: [],
            unprotectedMasterKeyHex: gen.masterKeyHex,
            createdAt: Date.now(),
            label: getDeviceNickname(kids.sigKid),
        }
        await saveWasmDevice(record)
        await deviceWorkerClient.unlock(gen.masterKeyHex)
        return record
    } finally {
        if (seedBytes) seedBytes.fill(0)
    }
}

// Promote the current unprotected device: wrap its master key under a
// passphrase and/or a freshly registered passkey, then drop the clear
// copy. Mirrors setupDevice steps 3–4 over an existing key. Nothing is
// persisted until the wrap succeeded, so a failure leaves the device
// exactly as it was (still unprotected, still usable).
export async function promoteDevice(opts: SetupDeviceOptions): Promise<WasmDeviceRecord> {
    const { passphrase } = opts
    if (!passphrase) throw new Error('passphrase required')
    const current = await loadCurrentWasmDevice()
    if (!current) throw new Error('promoteDevice: no device to promote')
    if (!current.unprotectedMasterKeyHex) throw new Error('promoteDevice: device is already protected')
    const wasm = await loadPracticemdWasm()
    const masterKeyHex = current.unprotectedMasterKeyHex

    const passphraseEnvelopeB64 = await wasm.wrapMasterKeyWithPassphrase(masterKeyHex, passphrase)
    const record = await promoteWasmDevice(current.seedKid, { passphraseEnvelopeB64, authenticators: [] })
    if (!(await deviceWorkerClient.isUnlocked())) {
        await deviceWorkerClient.unlock(masterKeyHex)
    }
    return record
}

// The ONE biometric of the reordered sign-up (Release 1, §7 step 3):
// a recovery passkey is enrolled for the ACCOUNT, and its PRF output is
// used twice — as this browser's local unlock key (the raw output wraps
// the master key, exactly as registerAuthenticator's envelope does) and,
// through the wasm's HKDF, as the key that wraps a freshly minted
// recovery device's seed into the recovery envelope. The result is the
// passkey entry of the recovery group finalizeUserCreation registers.
//
// Works on either kind of device this browser may hold at sign-up: an
// UNPROTECTED silent device (the anonymous /ava landing) is promoted in
// place — same seed, same sigKid — and a device with no record yet is set
// up as passkey-only. A device that is already protected only gains the
// recovery entry (the passkey is added as a further unlock method).
//
// Nothing is persisted until the ceremony has produced everything: cancel
// the sheet and the browser is exactly as it was, which is what lets the
// caller fall back to the guest path (O1).
export interface RecoveryPasskeyEnrolment {
    entry: {
        kind: 'passkey'
        seedHex: string
        name: string
        credentialId: string
        publicKey: string
        envelope: string
    }
    record: WasmDeviceRecord
}

// A passkey's name, the way a paper key is named (Appendix C, C2): "Passkey:
// <first two words>" of its recovery device's mnemonic — the same identifier
// the Recovery Kit prints for a paper key, disclosing no more than that. The
// Devices list appends the device nickname in parentheses itself, so it is
// not part of the stored name. The words are the only thing on that list
// that distinguishes one passkey from another: password managers show
// neither our display name nor anything else of ours (owner-verified,
// Apple Passwords and 1Password, 2026-09-22). Nobody names a passkey at
// enrolment: the recovery device is minted inside the ceremony and lives in
// memory for as short a time as possible (owner, 2026-09-22); a rename
// feature for any device is the place for a chosen name, if ever.
export function passkeyDefaultName(seedHex: string): string {
    try {
        const bytes = hexToBytes(seedHex)
        const words = seedToMnemonic(bytes)
        bytes.fill(0)
        return `Passkey: ${words[0]} ${words[1]}`
    } catch {
        return 'Passkey'
    }
}

export async function enrolRecoveryPasskeyForSignup(opts: {
    email: string
    /** For a browser that is ALREADY protected: the master key, recovered
     *  by re-authenticating (passphrase or an existing passkey — the
     *  Security section's helpers), so the new passkey becomes an unlock
     *  here too (Appendix C, C6). Absent, the browser keeps its methods
     *  and only recovery is added. Caller zeroes it. */
    masterKeyBytes?: Uint8Array
}): Promise<RecoveryPasskeyEnrolment> {
    const wasm = await loadPracticemdWasm()

    // 1. The recovery device's key material (no words shown), and the
    // passkey. The device's own kid is the credential's user handle (C1);
    // its first two words are the name.
    const prepared = await wasm.prepareRecoveryDevice()
    const name = passkeyDefaultName(prepared.seedHex)
    const enrolled = await enrollRecoveryPasskey({
        recoveryDeviceSeedKid: prepared.seedKid,
        email: opts.email,
        label: name,
    })
    const prf = enrolled.prfOutput
    try {
        // 2. The recovery envelope: the device seed under the HKDF key.
        const keyHex = await wasm.deriveRecoveryKey(bytesToHexString(prf))
        const envelope = await wasm.wrapRecoverySeed(prepared.seedHex, keyHex)

        // 3. This browser's device: protect it under the same passkey.
        const current = await loadCurrentWasmDevice()
        let record: WasmDeviceRecord
        const storedAuthr = async (masterKeyHex: string): Promise<StoredAuthenticator> => {
            const mk = hexToBytes(masterKeyHex)
            try {
                return {
                    credentialId: enrolled.credentialId,
                    envelopeB64: await wrapWithRawKey(prf, mk),
                    transports: enrolled.transports,
                    label: name,
                    createdAt: Date.now(),
                }
            } finally {
                mk.fill(0)
            }
        }
        if (current && current.unprotectedMasterKeyHex) {
            // The silent device, promoted in place.
            const authr = await storedAuthr(current.unprotectedMasterKeyHex)
            record = await promoteWasmDevice(current.seedKid, { authenticators: [authr] })
            if (!(await deviceWorkerClient.isUnlocked())) {
                await deviceWorkerClient.unlock(current.unprotectedMasterKeyHex)
            }
        } else if (current && opts.masterKeyBytes) {
            // Already protected, and the caller re-authenticated: the new
            // passkey becomes one more way to unlock this browser.
            const mk = opts.masterKeyBytes
            const authr: StoredAuthenticator = {
                credentialId: enrolled.credentialId,
                envelopeB64: await wrapWithRawKey(prf, mk),
                transports: enrolled.transports,
                label: name,
                createdAt: Date.now(),
            }
            const authenticators = [...current.authenticators, authr]
            await updateAuthenticators(current.seedKid, authenticators)
            record = { ...current, authenticators }
        } else if (current) {
            // Already protected, no re-authentication in hand: the browser
            // keeps the unlock methods it has. Adding one needs the master
            // key in the clear, and the worker holds it without ever
            // exporting it (C6). Recovery still works — the envelope is
            // minted regardless.
            record = current
        } else {
            // No device at all: passkey-only setup around this passkey.
            let seedBytes: Uint8Array | undefined
            let masterKeyHex = ''
            try {
                seedBytes = crypto.getRandomValues(new Uint8Array(32))
                const seedHex = bytesToHex(seedBytes)
                const kids = await wasm.deriveDeviceKids(seedHex)
                const gen = await wasm.generateMasterKeyAndSeedEnvelope(seedHex)
                masterKeyHex = gen.masterKeyHex
                const authr = await storedAuthr(masterKeyHex)
                record = {
                    seedKid: kids.seedKid, sigKid: kids.sigKid, encKid: kids.encKid, aesKid: kids.aesKid,
                    seedEnvelopeB64: gen.seedEnvelopeB64,
                    authenticators: [authr],
                    createdAt: Date.now(),
                    label: getDeviceNickname(kids.sigKid),
                }
                await saveWasmDevice(record)
                await deviceWorkerClient.unlock(masterKeyHex)
            } finally {
                if (seedBytes) seedBytes.fill(0)
                masterKeyHex = ''
            }
        }

        return {
            entry: {
                kind: 'passkey',
                seedHex: prepared.seedHex,
                name,
                credentialId: enrolled.credentialId,
                publicKey: enrolled.publicKey,
                envelope,
            },
            record,
        }
    } finally {
        prf.fill(0)
    }
}

// Protect this browser with a passkey the caller has ALREADY asserted —
// no second ceremony, no second credential.
//
// The restore path is the reason this exists. "Use Passkey" runs one
// discoverable assertion whose PRF output opens the recovery envelope; the
// account seed comes back and the browser then needs a device of its own to
// be provisioned. Without this, provisioning found no device, raised the
// setup prompter, and the person pressed their sensor a SECOND time to mint
// a SECOND credential — one for the account (user.id = userId) and one for
// the device (user.id = seedKid), both listed in their password manager.
// Every ceremony in this file evaluates PRF over the same salt, so the
// assertion the person just completed yields exactly the key the setup
// ceremony would have derived. Reuse it.
//
// No new exposure: whoever holds this credential can already unwrap the
// recovery seed and provision a device, so making it this browser's unlock
// grants nothing further.
//
// Three shapes, the same three the sign-up enrolment handles:
//   - no device here yet  → a passkey-only device around this credential;
//   - the silent device   → promoted in place (same seed, same sigKid, so
//                           an anonymous conversation survives);
//   - already protected   → left alone. Adding an unlock would need the
//                           master key in the clear and the worker does not
//                           export it; the browser keeps the methods it has.
//
// `prfOutput` stays the caller's to zero — it is still needed for the
// unwrap that precedes this call.
export async function adoptAssertedPasskeyAsUnlock(opts: {
    credentialId: string
    prfOutput: Uint8Array
    label?: string
    /** For an already-protected browser: the master key recovered by
     *  re-authentication, so the passkey is ADDED as an unlock (C6). */
    masterKeyBytes?: Uint8Array
}): Promise<{ record: WasmDeviceRecord; outcome: 'created' | 'promoted' | 'added' | 'kept' }> {
    const { credentialId, prfOutput } = opts
    if (prfOutput.length !== 32) {
        throw new Error(`adoptAssertedPasskeyAsUnlock: PRF output must be 32 bytes, got ${prfOutput.length}`)
    }
    const label = opts.label || 'Passkey'
    const storedAuthr = async (masterKeyHex: string): Promise<StoredAuthenticator> => {
        const mk = hexToBytes(masterKeyHex)
        try {
            return {
                credentialId,
                envelopeB64: await wrapWithRawKey(prfOutput, mk),
                label,
                createdAt: Date.now(),
            }
        } finally {
            mk.fill(0)
        }
    }

    const current = await loadCurrentWasmDevice()
    if (current && current.unprotectedMasterKeyHex) {
        const authr = await storedAuthr(current.unprotectedMasterKeyHex)
        const record = await promoteWasmDevice(current.seedKid, { authenticators: [authr] })
        if (!(await deviceWorkerClient.isUnlocked())) {
            await deviceWorkerClient.unlock(current.unprotectedMasterKeyHex)
        }
        return { record, outcome: 'promoted' }
    }
    if (current && opts.masterKeyBytes) {
        const authr: StoredAuthenticator = {
            credentialId,
            envelopeB64: await wrapWithRawKey(prfOutput, opts.masterKeyBytes),
            label,
            createdAt: Date.now(),
        }
        const authenticators = [...current.authenticators, authr]
        await updateAuthenticators(current.seedKid, authenticators)
        return { record: { ...current, authenticators }, outcome: 'added' }
    }
    if (current) {
        return { record: current, outcome: 'kept' }
    }

    const wasm = await loadPracticemdWasm()
    let seedBytes: Uint8Array | undefined
    let masterKeyHex = ''
    try {
        seedBytes = crypto.getRandomValues(new Uint8Array(32))
        const seedHex = bytesToHex(seedBytes)
        const kids = await wasm.deriveDeviceKids(seedHex)
        const gen = await wasm.generateMasterKeyAndSeedEnvelope(seedHex)
        masterKeyHex = gen.masterKeyHex
        const authr = await storedAuthr(masterKeyHex)
        const record: WasmDeviceRecord = {
            seedKid: kids.seedKid, sigKid: kids.sigKid, encKid: kids.encKid, aesKid: kids.aesKid,
            seedEnvelopeB64: gen.seedEnvelopeB64,
            authenticators: [authr],
            createdAt: Date.now(),
            label: getDeviceNickname(kids.sigKid),
        }
        await saveWasmDevice(record)
        await deviceWorkerClient.unlock(masterKeyHex)
        return { record, outcome: 'created' }
    } finally {
        if (seedBytes) seedBytes.fill(0)
        masterKeyHex = ''
    }
}

// The same one-biometric ceremony for an EXISTING account (the in-thread
// ask for a guest, Settings later): enrol, protect this browser, wrap the
// recovery device's seed — then register the device on the account
// (registerPasskeyDevice) rather than hand it to finalize. The registration
// is the caller's, because it goes through the device context; this returns
// the entry to register.
export async function enrolRecoveryPasskeyForAccount(opts: {
    email: string
    masterKeyBytes?: Uint8Array
}): Promise<RecoveryPasskeyEnrolment> {
    return enrolRecoveryPasskeyForSignup(opts)
}

// Runs the full passphrase setup ceremony programmatically — exposed so
// the React component and the e2e test harness share one implementation.
// Generates/imports the seed, wraps it with a fresh master key, wraps the
// master key with the passphrase, persists the WasmDeviceRecord, unlocks
// the Service Worker. Throws on any failure. Caller's responsibility to
// surface errors.
export async function setupDevice(opts: SetupDeviceOptions): Promise<WasmDeviceRecord> {
    const { passphrase } = opts
    if (!passphrase) throw new Error('passphrase required')
    const wasm = await loadPracticemdWasm()

    let seedBytes: Uint8Array | undefined
    let masterKeyHex = ''
    try {
        // 1. Obtain the seed.
        let seedHex: string
        let seedKid: string
        let sigKid = ''
        let encKid = ''
        let aesKid = ''
        let nickname: string | undefined

        const imported = await importLegacyDevice()

        console.log(`[DeviceUnlockSetup.setupDevice] resolve seed and nickname...`)
        if (imported) {
            console.log(`[DeviceUnlockSetup.setupDevice] resolving from legacy imported device`)
            seedBytes = imported.seedBytes
            seedHex = bytesToHex(seedBytes)
            seedKid = imported.seedKid
            sigKid = imported.sigKid
            encKid = imported.encKid
            aesKid = imported.aesKid
        } else {
            console.log(`[DeviceUnlockSetup.setupDevice] resolving from new seed`)
            seedBytes = crypto.getRandomValues(new Uint8Array(32))
            seedHex = bytesToHex(seedBytes)
            const kids = await wasm.deriveDeviceKids(seedHex)
            seedKid = kids.seedKid
            sigKid = kids.sigKid
            encKid = kids.encKid
            aesKid = kids.aesKid
        }
        nickname = getDeviceNickname(sigKid)
        console.log(`[DeviceUnlockSetup.setupDevice] resolved seed and nickname`, {seed: seedKid, nickname: nickname})

        // 2. Wrap the seed under a fresh master key.
        const gen = await wasm.generateMasterKeyAndSeedEnvelope(seedHex)
        masterKeyHex = gen.masterKeyHex

        // 3. Passphrase envelope.
        const passphraseEnvelopeB64 = await wasm.wrapMasterKeyWithPassphrase(masterKeyHex, passphrase)

        // 4. Persist + unlock.
        const record: WasmDeviceRecord = {
            seedKid,
            sigKid,
            encKid,
            aesKid,
            seedEnvelopeB64: gen.seedEnvelopeB64,
            passphraseEnvelopeB64,
            authenticators: [],
            createdAt: Date.now(),
            label: nickname,
        }
        await saveWasmDevice(record)
        await deviceWorkerClient.unlock(masterKeyHex)
        return record
    } finally {
        if (seedBytes) seedBytes.fill(0)
        masterKeyHex = ''
    }
}

// The form takes its colours from the page it sits on (owner's /design,
// 2026-10-16, "Follows the page"). A page states them as CSS custom
// properties, which the form inherits: the white sign-in surfaces
// (authorize.tsx's Container, AccountFlowDialog's FlowScope) set theirs. The
// fallbacks are the dark Settings page's, where the form was born, so a page
// that states nothing gets the form as it always was. Drawn for the dark
// ground alone, it was white on white wherever the protect step showed it on
// a sign-in page (tests/e2e/legacy-email-login-cold-browser.spec.ts).
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'var(--unlock-field, rgba(255, 255, 255, 0.05))',
    border: '1px solid var(--unlock-field-border, rgba(255, 255, 255, 0.15))',
    borderRadius: 6,
    color: 'var(--unlock-text, #fff)',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
}

const labelStyle: React.CSSProperties = {
    display: 'block',
    color: 'var(--unlock-label, rgba(255, 255, 255, 0.7))',
    fontSize: '0.85rem',
    marginBottom: '0.4rem',
}

// The Settings primary button's shape (Settings.css .settings-btn), its
// colours from the page. Its own rule rather than those classes: the
// sign-in surfaces style every bare `button` (#222, 100 px wide, at (0,1,1)),
// so the doubled class (0,2,0) is what keeps this one its own.
const SubmitButton = styled.button`
    && {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        width: auto;
        height: auto;
        padding: 0.625rem 1.25rem;
        border: none;
        border-radius: 6px;
        font-family: inherit;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        background: var(--unlock-button, #4299e1);
        color: var(--unlock-button-text, white);
    }
    &&:hover:not(:disabled) {
        background: var(--unlock-button-hover, #3182ce);
    }
    &&:disabled {
        background: var(--unlock-button-disabled, rgba(255, 255, 255, 0.1));
        color: var(--unlock-button-disabled-text, rgba(255, 255, 255, 0.4));
        cursor: not-allowed;
    }
`

// Passkey capability, probed once per mount. Three-state on purpose:
// `null` means "still asking", which is NOT the same as "no" — the probe is
// async, and treating unknown as no is what made the picker default to a mode
// the device could not perform.
export const DeviceUnlockSetup: React.FC<DeviceUnlockSetupProps> = ({
    onDone,
    onError,
    className,
    intro,
    submitLabel,
}) => {
    const [wasm, setWasm] = useState<PracticemdWASM | null>(null)
    // Starts at the mode EVERY device can perform. The default moves to
    // 'passkey' below once the probe says this device can — previously it
    // started at 'both' unconditionally, so a device without a platform
    // authenticator opened in a mode it could not complete and only said so
    // when Continue was pressed.
    const [passphrase, setPassphrase] = useState('')
    const [confirmPassphrase, setConfirmPassphrase] = useState('')
    const [showPassphrase, setShowPassphrase] = useState(false)
    const [busy, setBusy] = useState(false)
    const [err, setErr] = useState('')

    useEffect(() => {
        let cancelled = false
        loadPracticemdWasm().then(w => { if (!cancelled) setWasm(w) }).catch(e => {
            if (!cancelled) setErr(e instanceof Error ? e.message : String(e))
        })
        return () => { cancelled = true }
    }, [])

    const passphrasesMatch = passphrase.length > 0 && passphrase === confirmPassphrase

    const run = useCallback(async () => {
        if (!wasm) {
            setErr('WASM not ready yet.')
            return
        }
        if (!passphrase) {
            setErr('Enter a passphrase.')
            return
        }
        if (passphrase !== confirmPassphrase) {
            setErr("Passphrases don't match.")
            return
        }
        setErr('')
        setBusy(true)
        try {
            // The same UI serves two ceremonies: first-time setup (no
            // device) and PROMOTION of the anonymous landing's unprotected
            // device (a device exists, its master key is in the clear).
            const current = await loadCurrentWasmDevice()
            const record = current?.unprotectedMasterKeyHex
                ? await promoteDevice({ passphrase })
                : await setupDevice({ passphrase })
            setPassphrase('')
            setConfirmPassphrase('')
            onDone?.(record)
        } catch (e) {
            const error = (e instanceof Error ? e : new Error(String(e)))
            setErr(error.message)
            onError?.(error)
        } finally {
            setBusy(false)
        }
    }, [wasm, passphrase, confirmPassphrase, onDone, onError])

    return (
        <div className={className}>
            {intro && <div style={{ marginBottom: '0.75rem' }}>{intro}</div>}
            <>
            <>
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
                            <label style={{ ...labelStyle, marginBottom: 0 }}>New passphrase</label>
                            <button
                                type="button"
                                onClick={() => setShowPassphrase(s => !s)}
                                style={{
                                    // Its own size: the sign-in surfaces give
                                    // every bare button 100 x 40 px.
                                    width: 'auto',
                                    height: 'auto',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--unlock-muted, rgba(255, 255, 255, 0.6))',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                            >
                                {showPassphrase ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        <input
                            type={showPassphrase ? 'text' : 'password'}
                            value={passphrase}
                            onChange={e => setPassphrase(e.target.value)}
                            style={inputStyle}
                            autoComplete="new-password"
                        />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={labelStyle}>Confirm passphrase</label>
                        <input
                            type={showPassphrase ? 'text' : 'password'}
                            value={confirmPassphrase}
                            onChange={e => setConfirmPassphrase(e.target.value)}
                            style={inputStyle}
                            autoComplete="new-password"
                        />
                        <div
                            style={{
                                color: 'var(--unlock-error, #ff9f9f)',
                                fontSize: '0.8rem',
                                marginTop: '0.4rem',
                                // Always rendered so the Continue button doesn't
                                // jump when the message shows/hides.
                                visibility: confirmPassphrase.length > 0 && passphrase !== confirmPassphrase ? 'visible' : 'hidden',
                            }}
                        >
                            Passphrases don't match.
                        </div>
                    </div>
                </>
            <SubmitButton
                disabled={busy || !wasm || !passphrasesMatch}
                onClick={run}
            >
                {busy ? 'Working…' : (submitLabel || 'Protect this browser')}
            </SubmitButton>
            {err && (
                <div style={{ marginTop: '0.75rem', color: 'var(--unlock-error, #ff9f9f)', fontSize: '0.85rem' }}>
                    {err}
                </div>
            )}
            </>
        </div>
    )
}
